import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import CarrierBadge from '../carrier/CarrierBadge';
import { useLocation, useNavigate, useParams } from 'react-router';
import { installerApi, uploadViaSignedUrl } from '../../utils/installerApi';
import { useInstallerSession } from '../../hooks/useInstallerSession';
import { enqueueCompletion, isNetworkError } from '../../installer/pwa/offlineQueue';
import MapAddressPicker from './MapAddressPicker';
import CameraCapture from './CameraCapture';
import { InstallerTopbar } from './InstallerChrome';
import OfflineBanner from './OfflineBanner';
import { JobWarnings } from './InstallerFindings';
import InstallerOperations from './InstallerOperations';
import StageList from './StageList';
import JobMaterials from './JobMaterials';
import { JOB_KIND_LABELS, jobKindOf, WORK_FAMILIES, workFamilyOf } from '../../shared/installationProcess';
import { useOfflineQueue } from '../../installer/pwa/useOfflineQueue';
import '../../styles/InstallerPages.css';

const STATUS = {
  on_hold: {cls:'is-pending',label:'ناتمام؛ در انتظار تصمیم دفتر'},
  assigned: { cls: 'is-assigned', label: 'تخصیص‌داده‌شده' },
  in_progress: { cls: 'is-progress', label: 'در حال انجام' },
  completed: { cls: 'is-done', label: 'تکمیل‌شده' },
};

const COMPLETION_PHOTOS = [
  { type: 'cabling', label: 'عکس کابل‌کشی', required: true },
  { type: 'fat', label: 'عکس جعبه FAT', required: true },
  { type: 'equipment', label: 'عکس تجهیزات', required: false },
];

const BUNDLE_OUTCOMES = [
  { value: 'done', label: 'تحویل شد' },
  { value: 'failed', label: 'ناموفق بود' },
  { value: 'substituted', label: 'با قلم دیگری جایگزین شد' },
];

function jobBundleItems(job) {
  if (Array.isArray(job?.bundle_items)) return job.bundle_items;
  if (Array.isArray(job?.bundleItems)) return job.bundleItems;
  return [];
}

async function completeBundleJob(token, id, photos, note, bundleItemResults) {
  const response = await fetch(`/api/installer/jobs/${id}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Session-Token': token },
    body: JSON.stringify({ photos, note, bundleItemResults, capture: 'camera-v1' }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
  return body;
}

function JobStages({ token, job, onRefresh }) {
  const { drafts, outbox, notice } = useOfflineQueue(token, onRefresh);
  const reviewing = job.process.stages.some((s) => s.state === 'in_review');
  useEffect(() => {
    if (!reviewing) return undefined;
    const poll = () => { if (document.visibilityState !== 'hidden') onRefresh(); };
    const timer = setInterval(poll, 15000);
    document.addEventListener('visibilitychange', poll);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', poll); };
  }, [reviewing, onRefresh]);
  return <>{notice && <p role="status" className="inst-alert">{notice}</p>}
    <StageList completionPending={job.status !== 'completed'} jobId={job.id} stages={job.process.stages} drafts={drafts} outbox={outbox} /></>;
}

export default function InstallerJobDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { token } = useInstallerSession();
  const location = useLocation();
  const [submissionNotice, setSubmissionNotice] = useState(null);
  useEffect(() => {
    if (!location.state?.notice) return;
    setSubmissionNotice(location.state.notice);
    const { notice, ...state } = location.state;
    nav(`${location.pathname}${location.search}${location.hash}`, { replace: true, state });
  }, [location, nav]);
  const [job, setJob] = useState(null); const [err, setErr] = useState(null); const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState({ cabling: null, fat: null, equipment: null }); const [note, setNote] = useState('');
  const [bundleResults, setBundleResults] = useState({});
  const [camera, setCamera] = useState(null);

  const [revision, setRevision] = useState(0);
  const active = useRef(null); const request = useRef(0); const binding = `${token}:${id}`;
  const load = useCallback(() => {
    const serial = ++request.current;
    const current = () => active.current === binding && request.current === serial;
    return installerApi.job(token, id).then((r) => {
      if (!current()) return;
      setErr(null);
      setRevision((value) => value + 1);
      setJob(r.job);
      setBundleResults((current) => {
        const next = { ...current };
        for (const item of jobBundleItems(r.job)) {
          if (!next[item.bundleItemId]) {
            next[item.bundleItemId] = { state: '', error: '', substitutedItemId: '' };
          }
        }
        return next;
      });
    }).catch((e) => { if (current()) setErr(e.message); });
  }, [token, id, binding]);
  useEffect(() => {
    active.current = binding; setJob(null); setErr(null); setBundleResults({});
    if (!token) nav('/installer/login'); else load();
    return () => { active.current = null; request.current += 1; };
  }, [token, nav, load, binding]);

  const previews = useMemo(() => {
    const u = {};
    for (const t of ['cabling', 'fat', 'equipment']) if (photos[t]) u[t] = URL.createObjectURL(photos[t]);
    return u;
  }, [photos]);
  useEffect(() => () => Object.values(previews).forEach((url) => URL.revokeObjectURL(url)), [previews]);

  const bundleItems = jobBundleItems(job);
  const setBundleState = (bundleItemId, state) => {
    setBundleResults((current) => ({
      ...current,
      [bundleItemId]: {
        state,
        error: state === 'failed' ? (current[bundleItemId]?.error || '') : '',
        substitutedItemId: state === 'substituted'
          ? (current[bundleItemId]?.substitutedItemId || '')
          : '',
      },
    }));
  };
  const setBundleField = (bundleItemId, field, value) => {
    setBundleResults((current) => ({
      ...current,
      [bundleItemId]: { ...current[bundleItemId], [field]: value },
    }));
  };
  const itemResultReady = (item) => {
    const result = bundleResults[item.bundleItemId];
    if (!result || !BUNDLE_OUTCOMES.some((outcome) => outcome.value === result.state)) return false;
    if (result.state === 'failed') return result.error.trim().length >= 5;
    if (result.state === 'substituted') return Boolean(result.substitutedItemId.trim());
    return true;
  };
  const bundleResultsReady = bundleItems.every(itemResultReady);

  const start = async () => { setBusy(true); setErr(null); try { await installerApi.startJob(token, id); await load(); } catch (e) { setErr(e.message); } finally { setBusy(false); } };
  const complete = async () => {
    setBusy(true); setErr(null);
    const installerId = job.assigned_installer_id || 'job';
    const selected = ['cabling', 'fat', 'equipment'].filter((t) => photos[t]).map((t) => ({ type: t, file: photos[t] }));
    try {
      if (!photos.cabling || !photos.fat) throw new Error('عکسِ کابل‌کشی و جعبه FAT الزامی است.');
      if (!bundleResultsReady) throw new Error('نتیجه همه اقلام را مشخص کنید و برای مورد ناموفق علت دقیق بنویسید.');
      const bundleItemResults = bundleItems.map((item) => {
        const result = bundleResults[item.bundleItemId];
        return {
          bundleItemId: item.bundleItemId,
          state: result.state,
          error: result.state === 'failed' ? result.error.trim() : null,
          substitutedItemId: result.state === 'substituted' ? result.substitutedItemId.trim() : null,
        };
      });
      if (!navigator.onLine) {
        if (bundleItems.length) {
          throw new Error('برای ثبت نتیجه اقلام پکیج باید آنلاین باشید. پس از اتصال دوباره همین صفحه را ارسال کنید.');
        }
        await enqueueCompletion({ jobId: id, installerId, photos: selected.map(({ type, file }) => ({ type, blob: file })), note });
        nav('/installer/dashboard');
        return;
      }
      const uploaded = [];
      for (const { type, file } of selected) {
        const path = await uploadViaSignedUrl('job_photo', file, { session_token: token }, 'image');
        uploaded.push({ type, path });
      }
      if (bundleItems.length) {
        await completeBundleJob(token, id, uploaded, note, bundleItemResults);
      } else {
        await installerApi.completeJob(token, id, uploaded, note);
      }
      nav('/installer/dashboard');
    } catch (e) {
      if (isNetworkError(e) && !bundleItems.length) {
        await enqueueCompletion({ jobId: id, installerId, photos: selected.map(({ type, file }) => ({ type, blob: file })), note });
        nav('/installer/dashboard');
        return;
      }
      if (isNetworkError(e) && bundleItems.length) {
        setErr('اتصال قطع شد و نتیجه اقلام ارسال نشد. دوباره آنلاین شوید و همین صفحه را ارسال کنید.');
        return;
      }
      setErr(e.message);
    } finally { setBusy(false); }
  };

  if (!job) {
    return (
      <div className="inst"><div className="inst-shell">
        <InstallerTopbar />
        <div className="inst-card"><p className="inst-lead" style={{ margin: 0 }}>{err || 'در حال بارگذاری...'}</p></div>
      </div></div>
    );
  }

  const st = STATUS[job.status] || { cls: 'is-pending', label: job.status };
  // A civil job has no customer: the site label and its map pin say where the work is.
  const civil = workFamilyOf(job) === 'civil';
  const rows = [
    ['نوع کار', <><span className="inst-family">{WORK_FAMILIES[workFamilyOf(job)]}</span> · <span>{JOB_KIND_LABELS[jobKindOf(job)]}</span></>],
    !civil && ['مشتری', job.customer_name],
    !civil && ['تلفن', <a href={`tel:${job.customer_phone}`}>{job.customer_phone}</a>],
    [civil ? 'محل کار' : 'آدرس', job.customer_address_text],
    job.service_plan && ['پلن', job.service_plan],
    job.carrier && ['اپراتور', <CarrierBadge carrier={job.carrier} variant="inst" />],
    job.appointment_at && ['زمان قرار', new Date(job.appointment_at).toLocaleString('fa-IR',{timeZone:'Asia/Tehran'})],
    job.admin_note && ['یادداشت', job.admin_note],
  ].filter(Boolean);

  return (
    <div className="inst">
      <div className="inst-shell">
        <InstallerTopbar sub="جزئیات کار" />
        <OfflineBanner />
        <InstallerOperations key={`operations:${token}:${job.id}`} token={token} job={job} onRefresh={load} />
        <JobMaterials key={`materials:${token}:${job.id}`} token={token} job={job} />
        {submissionNotice && <p className="inst-ok" role="status">{submissionNotice}</p>}
        {err && <div role="alert" className="inst-alert">{err}</div>}

        <div className="inst-card">
          <div className="inst-jobcard-top" style={{ marginBottom: 14 }}>
            <h1 className="inst-h1" style={{ margin: 0 }}>جزئیات کار</h1>
            <span className={`inst-status ${st.cls}`}>{st.label}</span>
          </div>

          <div className="inst-detail">
            {rows.map(([k, v]) => (
              <div className="inst-row" key={k}>
                <span className="inst-row-k">{k}</span>
                <span className="inst-row-v">{v}</span>
              </div>
            ))}
          </div>

          {bundleItems.length > 0 && (
            <section aria-labelledby="bundle-items-title" data-testid="bundle-items"
              style={{ marginTop: 14, padding: 12, border: '2px solid #334155', borderRadius: 12, background: '#f8fafc', color: '#0f172a' }}>
              <h2 id="bundle-items-title" style={{ margin: '0 0 10px', fontSize: 17, fontWeight: 900 }}>اقلامی که باید تحویل شوند</h2>
              <ul style={{ display: 'grid', gap: 9, padding: 0, margin: 0, listStyle: 'none' }}>
                {bundleItems.map((item) => (
                  <li key={item.bundleItemId} data-testid={`bundle-item-${item.bundleItemId}`}
                    style={{ minWidth: 0, padding: 11, border: '1px solid #64748b', borderRadius: 10, background: '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, minWidth: 0 }}>
                      <strong style={{ minWidth: 0, fontSize: 16, lineHeight: 1.7, overflowWrap: 'anywhere' }}>{item.name}</strong>
                      <span style={{ flexShrink: 0, padding: '4px 9px', borderRadius: 999, background: item.required ? '#0f172a' : '#334155', color: '#fff', fontSize: 12, fontWeight: 900 }}>
                        {item.required ? 'اجباری' : 'اختیاری'}
                      </span>
                    </div>
                    {Number(item.quantity) > 1 && (
                      <div style={{ marginTop: 4, color: '#334155', fontWeight: 800 }}>
                        تعداد: {Number(item.quantity).toLocaleString('fa-IR')}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="inst-map" style={{ marginTop: 14 }}>
            <MapAddressPicker value={{ lat: job.customer_lat, lng: job.customer_lng }} onChange={() => {}} height={200} />
          </div>

          {job.status === 'assigned' && (
            <button className="inst-btn" style={{ marginTop: 16 }} disabled={busy} onClick={start}>
              {busy && <span className="inst-spin" />}شروع کار
            </button>
          )}
        </div>

        {job.process && <JobWarnings key={`${token}:${job.id}`} token={token} jobId={job.id} revision={revision} onAcknowledged={load} />}
        {job.process && <JobStages token={token} job={job} onRefresh={load} />}
        {job.status === 'in_progress' && !job.process && (
          <div className="inst-card">
            <h2 className="inst-h1" style={{ fontSize: 17 }}>تکمیل کار</h2>
            <p className="inst-lead">از کابل‌کشی و جعبهٔ FAT عکس بگیرید (الزامی)؛ عکسِ تجهیزات اختیاری است.</p>

            {bundleItems.length > 0 && (
              <section aria-labelledby="bundle-results-title" style={{ marginBottom: 18 }}>
                <h3 id="bundle-results-title" style={{ margin: '0 0 6px', color: '#0f172a', fontSize: 17, fontWeight: 900 }}>نتیجه تحویل هر قلم</h3>
                <p style={{ margin: '0 0 12px', color: '#334155', fontSize: 13.5, lineHeight: 1.8, fontWeight: 700 }}>
                  برای همه اقلام نتیجه را مشخص کنید. اگر موردی ناموفق بود، علت دقیق و قابل پیگیری بنویسید.
                </p>
                <div style={{ display: 'grid', gap: 12 }}>
                  {bundleItems.map((item) => {
                    const result = bundleResults[item.bundleItemId] || { state: '', error: '', substitutedItemId: '' };
                    const outcomeLabel = `نتیجه «${item.name}»`;
                    return (
                      <fieldset key={item.bundleItemId} data-testid={`bundle-result-${item.bundleItemId}`}
                        style={{ minWidth: 0, margin: 0, padding: 12, border: '2px solid #334155', borderRadius: 12, background: '#f8fafc', color: '#0f172a' }}>
                        <legend style={{ maxWidth: '100%', padding: '0 6px', color: '#0f172a', fontSize: 15.5, fontWeight: 900, overflowWrap: 'anywhere' }}>
                          {item.name}، {item.required ? 'اجباری' : 'اختیاری'}
                        </legend>
                        <label className="inst-label" htmlFor={`bundle-state-${item.bundleItemId}`} style={{ color: '#334155', fontSize: 13.5 }}>
                          {outcomeLabel}
                        </label>
                        <select className="inst-select" id={`bundle-state-${item.bundleItemId}`} aria-label={outcomeLabel}
                          value={result.state} onChange={(e) => setBundleState(item.bundleItemId, e.target.value)}>
                          <option value="">انتخاب نتیجه</option>
                          {BUNDLE_OUTCOMES.map((outcome) => <option key={outcome.value} value={outcome.value}>{outcome.label}</option>)}
                        </select>

                        {result.state === 'failed' && (
                          <div className="inst-field" style={{ marginTop: 12, marginBottom: 0 }}>
                            <label className="inst-label" htmlFor={`bundle-error-${item.bundleItemId}`} style={{ color: '#0f172a', fontSize: 13.5 }}>
                              علت دقیق ناموفق بودن «{item.name}»
                            </label>
                            <p style={{ margin: '0 0 7px', color: '#334155', fontSize: 12.5, lineHeight: 1.8, fontWeight: 700 }}>
                              مثال: «کالا موجود نبود» یا «فعال‌سازی با خطای ۴۰۳ رد شد». فقط «نشد» کافی نیست.
                            </p>
                            <textarea className="inst-textarea" id={`bundle-error-${item.bundleItemId}`}
                              aria-label={`علت دقیق ناموفق بودن «${item.name}»`} value={result.error}
                              onChange={(e) => setBundleField(item.bundleItemId, 'error', e.target.value)}
                              placeholder="علت، خطا یا مانعی را بنویسید که دفتر بتواند پیگیری کند." />
                          </div>
                        )}

                        {result.state === 'substituted' && (
                          <div className="inst-field" style={{ marginTop: 12, marginBottom: 0 }}>
                            <label className="inst-label" htmlFor={`bundle-substitute-${item.bundleItemId}`} style={{ color: '#0f172a', fontSize: 13.5 }}>
                              نام یا کد قلم جایگزین برای «{item.name}»
                            </label>
                            <input className="inst-input" id={`bundle-substitute-${item.bundleItemId}`}
                              aria-label={`نام یا کد قلم جایگزین برای «${item.name}»`} value={result.substitutedItemId}
                              onChange={(e) => setBundleField(item.bundleItemId, 'substitutedItemId', e.target.value)}
                              placeholder="مثلاً مودم مدل X یا کد کالای جایگزین" />
                          </div>
                        )}
                      </fieldset>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Camera only: the photo is the in-app frame, never a gallery file (no band on this frozen path). */}
            {COMPLETION_PHOTOS.map(({ type, label, required }) => (
              <div className="inst-field" key={type}>
                <span className="inst-label">{label} {required ? '*' : '(اختیاری)'}</span>
                {previews[type] && <img className="inst-photo-preview" src={previews[type]} alt={label} />}
                <button type="button" className="inst-btn inst-btn-ghost" disabled={busy}
                  aria-label={`${photos[type] ? 'گرفتن دوباره' : 'گرفتن'} ${label}`} onClick={() => setCamera({ type, label })}>
                  <Camera size={19} aria-hidden="true" />{photos[type] ? 'گرفتن دوباره' : 'گرفتن عکس'}
                </button>
              </div>
            ))}
            <CameraCapture open={Boolean(camera)} stamp={false} label={camera?.label} jobCode={String(job.id).slice(0, 6)}
              onClose={() => setCamera(null)}
              onShot={({ blob }) => {
                setPhotos((p) => ({ ...p, [camera.type]: new File([blob], `${camera.type}.jpg`, { type: 'image/jpeg' }) }));
                setCamera(null);
              }} />
            <div className="inst-field">
              <textarea className="inst-textarea" aria-label="یادداشت" value={note}
                onChange={(e) => setNote(e.target.value)} placeholder="یادداشت (اختیاری)" />
            </div>
            <button className="inst-btn" disabled={busy || !photos.cabling || !photos.fat || !bundleResultsReady} onClick={complete}>
              {busy && <span className="inst-spin" />}اتمام کار
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
